import { add, endOfDay, endOfMonth, format, isAfter, isBefore, startOfMonth } from 'date-fns';
import scheduleActions from '../schedulePageActions';
import { SHEET_EVENT } from 'subscriptions/events/events.enum';
import {
  addNewSheet,
  getResourcesTimeoffsAndHolidays,
  updateSheets,
  getWeekSheets,
  getMonthSheets,
} from '../schedulePageOperation';
import { subscriptionsFabric } from 'subscriptions/subscriptions.fabric';
import {
  worklogsActions,
  selectActiveSheetsFlat,
  selectSubmittedSheetsFlat,
  selectForemanSheetsFlat,
} from 'store/worklogs';
import { getMonthDatesByFullWeeksAsKeys, isMobileSchedule } from 'helpers/_helpers';
import { getSheetResourceDictionaries, getSheetResourceUpdates } from 'subscriptions/utils';
import { SCHEDULE_TYPES, SCHEDULE_VIEW_TYPES } from 'pages/ScheduleV6/constants/constants';
import { globalBEDateFormat } from 'common/dateFormatConfig';

// Returns the current worklog tab's flat sheet list (derived from the entity store) + its refresh action.
export const getStoredWorklogs = (currentURL, store) => {
  const state = store.getState();

  if (currentURL.includes('worklog/active')) {
    return { storedSheets: selectActiveSheetsFlat(state), action: worklogsActions.setRefreshActiveSheet };
  }
  if (currentURL.includes('worklog/submissions')) {
    return { storedSheets: selectSubmittedSheetsFlat(state), action: worklogsActions.setRefreshSubmittedSheet };
  }
  if (currentURL.includes('worklog/worklog-foreman')) {
    return { storedSheets: selectForemanSheetsFlat(state), action: worklogsActions.setRefreshCrewSheet };
  }
  return { storedSheets: [], action: undefined };
};

export const worklogPageFlowHelper = (store, data, noPublishCheck = true) => {
  const currentURL = window.location.href;
  if (!currentURL.includes('worklog')) return;

  const { storedSheets, action } = getStoredWorklogs(currentURL, store);

  if (!storedSheets.length || !action) return;

  const sheetInWorklogPage = storedSheets.find((sheet) => sheet._id === data._id);

  if (sheetInWorklogPage && (noPublishCheck || data.published)) {
    store.dispatch(action({ ...sheetInWorklogPage, ...data }));
  }
};

const handlers = {
  [SHEET_EVENT.created]: (store) => (data) => {
    // sheet obj
    const { scheduleView, selectedDate, currentMonthDate } = store.getState().schedule;
    const { weekStart } = store.getState().personalProfile.organization.settings;
    const dateFormat = store.getState().personalProfile.organization?.settings?.dateFormat;

    if (scheduleView === SCHEDULE_VIEW_TYPES.monthly) {
      let dateKey = '';

      if (data.travelTimeOnly) {
        dateKey = format(new Date(data.startTimeDate || data.createdAt), dateFormat);
      } else {
        dateKey = format(new Date(data.hours.start), dateFormat);
      }
      const currentDateRangeOnMonthlyCalendar = getMonthDatesByFullWeeksAsKeys(
        currentMonthDate,
        weekStart === 'Sun' ? 0 : 1,
        dateFormat
      ); // make empty cells
      if (currentDateRangeOnMonthlyCalendar[dateKey]) {
        store.dispatch(scheduleActions.addSheetToMonthSchedule({ dateKey, data }));
      }
    } else if (scheduleView === SCHEDULE_VIEW_TYPES.weekly) {
      const date = data.travelTimeOnly
        ? new Date(data.startTimeDate || data.createdAt)
        : new Date(data.hours.start);
      const dateKey = format(date, globalBEDateFormat);
      if (isAfter(date, selectedDate) && isBefore(date, add(new Date(selectedDate), { days: 7 }))) {
        store.dispatch(scheduleActions.addSheetToWeekSchedule({ dateKey, data }));
      }
    } else {
      if (data._id) store.dispatch(updateSheets());

      const currentURL = window.location.href;
      if (currentURL.includes('worklog')) store.dispatch(worklogsActions.setForceUpdate(true));
    }
  },
  [SHEET_EVENT.updated]: (store) => (data) => {
    const { sheets, scheduleView, selectedDate, currentMonthDate } = store.getState().schedule;
    const { weekStart } = store.getState().personalProfile.organization.settings;
    const sheetInSchedule = sheets.find(({ sheet }) => sheet._id === data?._id);
    const dateFormat = store.getState().personalProfile.organization?.settings?.dateFormat;

    if (scheduleView === SCHEDULE_VIEW_TYPES.monthly) {
      let dateKey = '';

      if (data.travelTimeOnly) {
        dateKey = format(new Date(data.startTimeDate || data.createdAt), dateFormat);
      } else {
        dateKey = format(new Date(data.hours.start), dateFormat);
      }
      const currentDateRangeOnMonthlyCalendar = getMonthDatesByFullWeeksAsKeys(
        currentMonthDate,
        weekStart === 'Sun' ? 0 : 1,
        dateFormat
      ); // make empty cells
      if (currentDateRangeOnMonthlyCalendar[dateKey]) {
        store.dispatch(scheduleActions.removeSheetFromMonthSchedule(data?._id));
        store.dispatch(addNewSheet(data));
        store.dispatch(scheduleActions.addSheetToMonthSchedule({ dateKey, data }));
      }
    } else if (scheduleView === SCHEDULE_VIEW_TYPES.weekly) {
      const date = data.travelTimeOnly
        ? new Date(data.startTimeDate || data.createdAt)
        : new Date(data.hours.start);

      if (isAfter(date, new Date(selectedDate)) && isBefore(date, add(new Date(selectedDate), { days: 7 }))) {
        store.dispatch(addNewSheet(data));
        store.dispatch(scheduleActions.updateWeekSheet(data));
      }
    }

    if (data._id && sheetInSchedule) {
      store.dispatch(scheduleActions.updateSheet(data));
    }

    worklogPageFlowHelper(store, data);
  },
  [SHEET_EVENT.resourcesAvailability]: (store) => (data) => {
    const {
      selectedDate,
      currentMonthDate,
      scheduleView,
      scheduleDayView,
      showAllLocations,
    } = store.getState().schedule;
    let sheets = [];
    if (scheduleView === SCHEDULE_VIEW_TYPES.monthly) {
      if (
        new Date(data.to).getTime() < startOfMonth(currentMonthDate).getTime() ||
        new Date(data.from).getTime() > endOfMonth(currentMonthDate).getTime()
      ) return;

      const sheetsByDate = store.getState().schedule.monthSheets;
      sheets = [];
      for (const day in sheetsByDate) {
        sheets.push(...(sheetsByDate[day] || []))
      }
    } else if (scheduleView === SCHEDULE_VIEW_TYPES.weekly) {
      if (
        isBefore(data.to, new Date(selectedDate)) || isAfter(data.from, add(new Date(selectedDate), { days: 7 }))
      ) return;

      const sheetsByDate = store.getState().schedule.weekSheets;
      sheets = [];
      for (const day in sheetsByDate) {
        sheets.push(...(sheetsByDate[day] || []))
      }
    } else { // Schedule type !== monthly
      if (
        new Date(data.from)?.getTime() > endOfDay(selectedDate).getTime() ||
        new Date(data.to)?.getTime() < selectedDate.getTime()
      ) return;
      sheets = store.getState().schedule.sheets.map(sheet => (sheet?.sheet || sheet));
    }

    const { updatedWorkersBySheet, updatedEquipmentBySheet } = getSheetResourceDictionaries(
      sheets,
      data.workers,
      data.equipment
    );

    for (const sheet of sheets) {
      const sheetUpdatedWorkers = updatedWorkersBySheet[sheet._id];
      const sheetUpdatedEquipment = updatedEquipmentBySheet[sheet._id];
      // if (!sheetUpdatedWorkers?.workers?.length && !sheetUpdatedEquipment?.equipment?.length) continue;
      
      const { workers, equipment } = getSheetResourceUpdates(sheet, sheetUpdatedWorkers?.workers, sheetUpdatedEquipment?.equipment);
      if (scheduleView === SCHEDULE_VIEW_TYPES.monthly) {
        store.dispatch(scheduleActions.updateMonthSheet({_id: sheet._id, workers, equipment }));
      } else if (scheduleView === SCHEDULE_VIEW_TYPES.weekly) {
        store.dispatch(scheduleActions.updateWeekSheet({_id: sheet._id, workers, equipment }));
      } else {
        store.dispatch(scheduleActions.updateSheet({
          ...(sheet || {}),
          workers,
          equipment,
        }));
      }
    }


    const currentURL = window.location.href;
    if (currentURL.includes('worklog/worklog-foreman')) {
      store.dispatch(worklogsActions.setForceUpdate(true));
    } else {
      store.dispatch(getResourcesTimeoffsAndHolidays({
        date: selectedDate,
        isGridView: scheduleDayView === SCHEDULE_TYPES.grid,
        showAllLocations,
        scheduleView,
      }))
    }
  },
  [SHEET_EVENT.updatedBunch]: (store) => (data) => {
    const dateFormat = store.getState().personalProfile.organization?.settings?.dateFormat;
    const formattedDate = format(store.getState().schedule.selectedDate, dateFormat);

    if (data.dates.includes(formattedDate)) store.dispatch(updateSheets());
  },
  [SHEET_EVENT.deleted]: (store) => (data) => {
    if (data._id) store.dispatch(scheduleActions.deleteWorklog(data));

    const currentURL = window.location.href;
    if (currentURL.includes('worklog/active')) {
      store.dispatch(worklogsActions.setDeletedActiveSheet(data?._id));
    } else if (currentURL.includes('worklog/worklog-foreman')) {
      store.dispatch(worklogsActions.setDeletedCrewSheet(data?._id));
    }
  },
  [SHEET_EVENT.submissionDeleted]: (store) => (data) => {
    const { sheets, scheduleView } = store.getState().schedule;
    const sheetInSchedule = sheets.find(({ sheet }) => sheet._id === data?._id);

    if (scheduleView === SCHEDULE_VIEW_TYPES.weekly) {
      store.dispatch(scheduleActions.updateWeekSheet({
        _id: data._id,
        submittedAt: data.submittedAt,
        submittedBy: data.submittedBy,
      }));
    }

    if (data._id && sheetInSchedule) {
      store.dispatch(
        scheduleActions.setSheetSubmitted({ _id: data._id, submittedAt: null, submittedBy: null })
      );
    }
    const currentURL = window.location.href;
    if (currentURL.includes('worklog/submissions')) {
      store.dispatch(worklogsActions.setForceUpdate(true));
    }
  },
  [SHEET_EVENT.returnToShopChanged]: (store) => (data) => {
    const sheets = store.getState().schedule.sheets;
    const sheetInSchedule = sheets.find(({ sheet }) => sheet._id === data?._id);
    if (data._id && sheetInSchedule) store.dispatch(scheduleActions.setReturnToShop(data));
  },
  [SHEET_EVENT.notes]: (store) => (data) => {
    const { sheets, scheduleView } = store.getState().schedule;
    const sheetInSchedule = sheets.find(({ sheet }) => sheet._id === data?._id);

    if (scheduleView === SCHEDULE_VIEW_TYPES.weekly) {
      store.dispatch(scheduleActions.updateWeekSheet({
        _id: data._id,
        submittedAt: data.submittedAt,
        submittedBy: data.submittedBy,
      }));
    }

    if (sheetInSchedule) {
      store.dispatch(scheduleActions.updateSheet({ ...sheetInSchedule.sheet, ...data }));
    }
  },
  [SHEET_EVENT.updatedCrewLeader]: (store) => (data) => {
    const { sheets, scheduleView } = store.getState().schedule;
    const sheetInSchedule = sheets.find(({ sheet }) => sheet._id === data?._id);
    const workerData = {
      _id: data?._id,
      workerId: data?.worker?._id,
      foreman: data?.worker?.foreman,
    };

    store.dispatch(scheduleActions.updateCrewLeaderMonthCalendar(workerData));
    if (scheduleView === SCHEDULE_VIEW_TYPES.weekly) {
      store.dispatch(scheduleActions.updateCrewLeaderWeekCalendar(workerData));
    }
    if (data._id && sheetInSchedule) {
      store.dispatch(scheduleActions.updateCrewLeader(workerData));
    }
  },
  [SHEET_EVENT.canceled]: (store) => (data) => {
    const { scheduleView } = store.getState().schedule;
    if (data._id && data.canceledAt && data.canceledBy) {
      const sheets = store.getState().schedule.sheets;
      const sheetInSchedule = sheets.find(({ sheet }) => sheet._id === data?._id);
      if (sheetInSchedule) store.dispatch(scheduleActions.setSheetCanceled(data));
      if (scheduleView === SCHEDULE_VIEW_TYPES.monthly) {
        store.dispatch(scheduleActions.updateMonthSheet(data));
      }
      if (scheduleView === SCHEDULE_VIEW_TYPES.weekly) {
        store.dispatch(scheduleActions.updateWeekSheet(data));
      }
      worklogPageFlowHelper(store, data);
    }
  },
  [SHEET_EVENT.uncanceled]: (store) => (data) => {
    const { scheduleView } = store.getState().schedule;
    if (data._id) {
      const sheets = store.getState().schedule.sheets;
      const sheetInSchedule = sheets.find(({ sheet }) => sheet._id === data?._id);
      const uncanceled = {
        _id: data._id,
        canceledAt: null,
        canceledBy: null,
        cancelNote: null,
      };
      if (sheetInSchedule) store.dispatch(scheduleActions.setSheetCanceled(uncanceled));
      if (scheduleView === SCHEDULE_VIEW_TYPES.monthly) {
        store.dispatch(scheduleActions.updateMonthSheet(uncanceled));
      }
      if (scheduleView === SCHEDULE_VIEW_TYPES.weekly) {
        store.dispatch(scheduleActions.updateWeekSheet(data));
      }

      worklogPageFlowHelper(store, uncanceled);
    }
  },
  [SHEET_EVENT.submitted]: (store) => (data) => {
    const sheets = store.getState().schedule.sheets;
    const sheetInSchedule = sheets.find(({ sheet }) => sheet._id === data?._id);
    if (sheetInSchedule && data._id && data.submittedAt && data.submittedBy) {
      store.dispatch(scheduleActions.setSheetSubmitted(data));
    }

    const currentURL = window.location.href;
    if (currentURL.includes('worklog/submissions'))
      store.dispatch(worklogsActions.setForceUpdate(true));
  },
  [SHEET_EVENT.reconciledBunch]: (store) => (data) => {
    if (Array.isArray(data)) {
      store.dispatch(scheduleActions.reconcileWorklogs({ sheetIds: data.map(({ _id }) => _id) }));
    }
  },
  [SHEET_EVENT.unpublished]: (store) => (data) => {
    if (data?.length) {
      for (const sheetObject of data) {
        store.dispatch(scheduleActions.unpublishWorklog(sheetObject));
      }
    }
  },
  [SHEET_EVENT.publishedBunch]: (store) => (data) => {
    if (Array.isArray(data)) {
      const { scheduleView } = store.getState().schedule;
      store.dispatch(scheduleActions.publishWorklogs({ sheetIds: data.map(({ _id }) => _id) }));

      const published = {
        ...data[0],
      };
      if (scheduleView === SCHEDULE_VIEW_TYPES.monthly) {
        store.dispatch(scheduleActions.updateMonthSheet(published));
      }
      if (scheduleView === SCHEDULE_VIEW_TYPES.weekly) {
        store.dispatch(scheduleActions.updateWeekSheet(published));
      }
      

      const currentURL = window.location.href;
      if (!currentURL.includes('worklog')) return;

      const { storedSheets } = getStoredWorklogs(currentURL, store);
      const sheetInWorklogPage = storedSheets.find((sheet) => sheet._id === data._id);

      if (sheetInWorklogPage) {
        worklogPageFlowHelper(store, {
          ...sheetInWorklogPage,
          published: true,
          unpublishedChanges: false,
        });
      }
    }
  },
  [SHEET_EVENT.gridPositionChanged]: (store) => (data) => {
    if (!data?.length || !Array.isArray(data)) return;

    if (data.length > 1) {
      const swappedGrids = data
        .filter((data) => data._id && data.gridPosition >= 0)
        .map((data) => ({ _id: data._id, position: parseInt(data.gridPosition) }));
      store.dispatch(scheduleActions.swapGridsPosition({ swappedGrids }));
    } else if (data?.[0]?._id && data?.[0]?.gridPosition >= 0) {
      const actionData = { _id: data[0]._id, position: parseInt(data[0].gridPosition) };
      store.dispatch(scheduleActions.setGridPosition(actionData));
    }
  },
};

const refetchSchedule = (store) => {
  // the Worklogs page shares this middleware but doesn't render schedule state
  if (window.location.pathname.includes('worklog')) return;

  const { scheduleView, filters, showAllLocations } = store.getState().schedule;
  const params = { filters: { ...filters, allLocations: showAllLocations } };

  // mobile renders the day list for every scheduleView
  if (isMobileSchedule() || scheduleView === SCHEDULE_VIEW_TYPES.daily) store.dispatch(updateSheets());
  else if (scheduleView === SCHEDULE_VIEW_TYPES.weekly) store.dispatch(getWeekSheets(params));
  else store.dispatch(getMonthSheets(params));
};

export const sheetsSubscriptionMiddleware = subscriptionsFabric(
  scheduleActions,
  SHEET_EVENT,
  handlers,
  {
    assignedToLocations: (store) => store.getState().schedule.showAllLocations,
    onReconnect: refetchSchedule,
  }
);
